import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { Card, Col, Radio, Row } from "antd";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { hasPermission } from "../../../common/security/utils";
import { usePermissions } from "../../auth/hooks";
import { DepositContractForm } from "../components/forms/deposit/DepositContractForm";
import { GenericContractForm } from "../components/forms/generic/GenericContractForm";
import { InsuranceContractForm } from "../components/forms/insurance/InsuranceContractForm";
import { InvestmentContractForm } from "../components/forms/investment/InvestmentContractForm";
import { LoanContractForm } from "../components/forms/loan/LoanContractForm";
import { SecondPillarContractForm } from "../components/forms/secondpillar/SecondPillarContractForm";
import { ThirdPillarContractForm } from "../components/forms/thirdpillar/ThirdPillarContractForm";
import { ContractType } from "../enums";

export const ContractCreateContainer = () => {
  const { t } = useTranslation();

  const permissions = usePermissions();

  const allowedContractTypes = useMemo(() => {
    const contractTypes: ContractType[] = [];

    if (hasPermission(permissions, Permission.INSURANCE_CREATE)) {
      contractTypes.push(ContractType.INSURANCE_CONTRACT);
    }
    if (hasPermission(permissions, Permission.LOAN_CREATE)) {
      contractTypes.push(ContractType.LOAN_CONTRACT);
    }
    if (hasPermission(permissions, Permission.INVESTMENT_CREATE)) {
      contractTypes.push(ContractType.INVESTMENT_CONTRACT);
    }
    if (hasPermission(permissions, Permission.DEPOSIT_CREATE)) {
      contractTypes.push(ContractType.DEPOSIT_CONTRACT);
    }
    if (hasPermission(permissions, Permission.SECOND_PILLAR_CREATE)) {
      contractTypes.push(ContractType.SECOND_PILLAR_CONTRACT);
    }
    if (hasPermission(permissions, Permission.THIRD_PILLAR_CREATE)) {
      contractTypes.push(ContractType.THIRD_PILLAR_CONTRACT);
    }
    if (hasPermission(permissions, Permission.GENERIC_CREATE)) {
      contractTypes.push(ContractType.GENERIC_CONTRACT);
    }

    return contractTypes;
  }, [permissions]);

  const [contractType, setContractType] = useState<ContractType | undefined>(
    allowedContractTypes.length === 1 ? allowedContractTypes[0] : undefined,
  );

  let content: React.ReactNode;
  switch (contractType) {
    case ContractType.INSURANCE_CONTRACT:
      content = <InsuranceContractForm />;
      break;
    case ContractType.LOAN_CONTRACT:
      content = <LoanContractForm />;
      break;
    case ContractType.INVESTMENT_CONTRACT:
      content = <InvestmentContractForm />;
      break;
    case ContractType.DEPOSIT_CONTRACT:
      content = <DepositContractForm />;
      break;
    case ContractType.SECOND_PILLAR_CONTRACT:
      content = <SecondPillarContractForm />;
      break;
    case ContractType.THIRD_PILLAR_CONTRACT:
      content = <ThirdPillarContractForm />;
      break;
    case ContractType.GENERIC_CONTRACT:
      content = <GenericContractForm />;
      break;
    default:
      content = (
        <div className="center-align sub-header-info dashed">{t("contract.helpers.noContractTypeSelected")}</div>
      );
      break;
  }

  return (
    <ContentWrapper>
      <Card
        className="card-box"
        title={
          <BackNavigationArrow>
            <h2>{t("contract.titles.createContract")}</h2>
          </BackNavigationArrow>
        }
      >
        <Row gutter={rowGutter} justify="center">
          <Col span={24} className="center-align">
            <Radio.Group
              value={contractType}
              size="large"
              buttonStyle="solid"
              optionType="button"
              block
              onChange={(e) => setContractType(e.target.value)}
            >
              {allowedContractTypes.map((type) => (
                <Radio.Button value={type} key={type}>
                  <span style={{ fontSize: 16, fontWeight: "bold" }}>
                    {t("contract.actions.createContractType." + type)}
                  </span>
                </Radio.Button>
              ))}
            </Radio.Group>
          </Col>
        </Row>
      </Card>
      {content}
    </ContentWrapper>
  );
};
